import { useModal } from '@ebay/nice-modal-react';
import { useEffect, useRef } from 'react';

import {
  isLoginSecretStorageUnlockActive,
  isLoginUnlockResetCompleted,
} from 'contexts/MatrixContext/loginSecretStorageUnlock';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import { MatrixLoggingInStatusEnum } from 'contexts/MatrixContext/utils';
import { CrossSigningStatusEnum } from 'contexts/MatrixDeviceContext/types';
import useMatrixDeviceContext from 'contexts/MatrixDeviceContext/useMatrixDeviceContext';
import useMatrixDeviceVerificationContext from 'contexts/MatrixDeviceVerificationContext/useMatrixDeviceVerificationContext';
import { useSessionState, SessionStateKeys } from 'hooks/useSessionState';
import AccountSettingsModal from 'modules/Authentication/Settings/AccountSettings';
import ChooseVerificationMethodModal from 'modules/EndToEndEncryption/ChooseVerificationMethodModal/ChooseVerificationMethodModal';
import ResetKeysModal from 'modules/EndToEndEncryption/ResetKeysModal/ResetKeysModal';
import CreateSecurityCodeModal from 'modules/EndToEndEncryption/SecurityCode/CreateSecurityCodeModal/CreateSecurityCodeModal';

const useVerificationMethodSelection = () => {
  const { crossSigningStatus, hasCompletedSelfVerification, activeRequest } = useMatrixDeviceContext();
  const { hasChosenSasVerification } = useMatrixDeviceVerificationContext();
  const { matrixLoggingInStatus, hasReachedSyncReadyState } = useMatrixContext({
    requireDefinedMatrixClient: false,
  });
  const { visible: isAccountSettingsModalOpen } = useModal(AccountSettingsModal);
  const { visible: isResetKeysModalVisible } = useModal(ResetKeysModal);
  const { visible: isCreateSecurityCodeModalVisible } = useModal(CreateSecurityCodeModal);
  const chooseVerificationMethodModal = useModal(ChooseVerificationMethodModal);
  const [needsResetAfterLogin] = useSessionState<boolean>(SessionStateKeys.NEEDS_RESET_KEYS_AFTER_LOGIN, false);
  const [needsSecurityCodePromptAfterBootstrap, , clearSecurityCodePrompt] = useSessionState<boolean>(
    SessionStateKeys.NEEDS_SECURITY_CODE_PROMPT_AFTER_BOOTSTRAP,
    false
  );

  const hasShownModalRef = useRef(false);
  const prevCrossSigningStatusRef = useRef(crossSigningStatus);

  useEffect(() => {
    if (isLoginSecretStorageUnlockActive()) return;

    const matrixDidNotFinishLoading =
      matrixLoggingInStatus !== MatrixLoggingInStatusEnum.FINISHED || !hasReachedSyncReadyState;
    const isCrossSigningStatusUnknown = crossSigningStatus === CrossSigningStatusEnum.UNKNOWN;
    if (isAccountSettingsModalOpen || matrixDidNotFinishLoading || isCrossSigningStatusUnknown) return;

    const becameNotVerified =
      crossSigningStatus === CrossSigningStatusEnum.NOT_VERIFIED &&
      prevCrossSigningStatusRef.current !== CrossSigningStatusEnum.NOT_VERIFIED;

    // Re-arm the picker when this device becomes unverified (e.g. another device reset keys — BUG-022).
    let suppressPickerForReset = needsSecurityCodePromptAfterBootstrap === true || isCreateSecurityCodeModalVisible;
    if (becameNotVerified) {
      hasShownModalRef.current = false;
      // A stale create-code flag from a different flow must not suppress the picker on an idle device
      // that was remotely invalidated (Faro nBXJcAxtEa). Only keep it during login-unlock reset.
      if (needsSecurityCodePromptAfterBootstrap && !isLoginUnlockResetCompleted()) {
        clearSecurityCodePrompt();
        // clearSecurityCodePrompt updates session state asynchronously — don't let the stale
        // captured value suppress the picker in this same effect run.
        suppressPickerForReset = isCreateSecurityCodeModalVisible;
      }
    }
    prevCrossSigningStatusRef.current = crossSigningStatus;

    if (crossSigningStatus === CrossSigningStatusEnum.VERIFIED) {
      if (hasShownModalRef.current) chooseVerificationMethodModal.remove();
      hasShownModalRef.current = false;
      return;
    }

    // Don't auto-show the verification picker while a post-login key reset is pending or actively
    // in progress — PostLoginResetGate shows ResetKeysModal instead. Showing both simultaneously
    // produces confusing UX (BUG-015 Fix B side-effect: bootstrapCrossSigning is skipped for the
    // deferred-reset path, so crossSigningStatus briefly reads NOT_VERIFIED post-login).
    if (needsResetAfterLogin || isResetKeysModalVisible) return;

    // After a successful reset on THIS device, create-code is scheduled and/or the reset completed
    // this login session. Suppress the picker so BootstrapSecurityCodeGate can show create-code
    // instead (BUG-019). Also suppress while the create-code modal is open (BUG-021).
    // Do NOT use isLoginUnlockResetCompleted alone — it is not cleared on remote invalidation and
    // would block the picker on idle devices until reload (BUG-022 / Faro nBXJcAxtEa).
    if (suppressPickerForReset) {
      if (hasShownModalRef.current) chooseVerificationMethodModal.remove();
      hasShownModalRef.current = false;
      return;
    }

    if (
      crossSigningStatus === CrossSigningStatusEnum.NOT_VERIFIED &&
      !hasCompletedSelfVerification &&
      !hasChosenSasVerification &&
      !activeRequest &&
      !hasShownModalRef.current
    ) {
      hasShownModalRef.current = true;
      chooseVerificationMethodModal.show();
    }
  }, [
    crossSigningStatus,
    hasCompletedSelfVerification,
    hasChosenSasVerification,
    activeRequest,
    chooseVerificationMethodModal,
    isAccountSettingsModalOpen,
    matrixLoggingInStatus,
    hasReachedSyncReadyState,
    needsResetAfterLogin,
    isResetKeysModalVisible,
    needsSecurityCodePromptAfterBootstrap,
    isCreateSecurityCodeModalVisible,
    clearSecurityCodePrompt,
  ]);
};

export default useVerificationMethodSelection;
